/**
 * Normal-distribution primitives, shared by the API and the dashboard.
 *
 * These live in helpers rather than beside the server's other statistics code because
 * both sides need them: the sample-ratio check and the p-value series are computed in
 * the browser from data already on the page. A second copy in the dashboard would be
 * the same drift that let a stale PowerAnalysisResult and a broken erf survive, the
 * erf on master was wrong by 0.21 and every test agreed with it.
 *
 * The server re-exports these from methods/distributions so its own imports are
 * unchanged.
 */
export const erf = (x: number): number => {
  const sign = x >= 0 ? 1 : -1;
  const ax = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * ax);
  const poly = t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
  return sign * (1 - poly * Math.exp(-ax * ax));
};

/**
 * Standard normal CDF: P(Z <= z)
 */
export const normalCDF = (z: number): number => (1 + erf(z / Math.sqrt(2))) / 2;

/**
 * Standard normal quantile (inverse CDF) using Beasley-Springer-Moro approximation.
 * Accurate to ~1e-6 for p in (0.0001, 0.9999).
 */
export const normalQuantile = (p: number): number => {
  if (p <= 0) return -Infinity;
  if (p >= 1) return Infinity;
  if (p === 0.5) return 0;

  const a = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2, -3.066479806614716e1,
    2.506628277459239,
  ];
  const b = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1, -1.328068155288572e1,
  ];
  const c = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734, 4.374664141464968,
    2.938163982698783,
  ];
  const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];

  const pLow = 0.02425;
  const pHigh = 1 - pLow;

  let q: number;
  let r: number;

  if (p < pLow) {
    q = Math.sqrt(-2 * Math.log(p));
    return (
      (((((c[0]! * q + c[1]!) * q + c[2]!) * q + c[3]!) * q + c[4]!) * q + c[5]!) /
      ((((d[0]! * q + d[1]!) * q + d[2]!) * q + d[3]!) * q + 1)
    );
  } else if (p <= pHigh) {
    q = p - 0.5;
    r = q * q;
    return (
      ((((((a[0]! * r + a[1]!) * r + a[2]!) * r + a[3]!) * r + a[4]!) * r + a[5]!) * q) /
      (((((b[0]! * r + b[1]!) * r + b[2]!) * r + b[3]!) * r + b[4]!) * r + 1)
    );
  } else {
    q = Math.sqrt(-2 * Math.log(1 - p));
    return (
      -(((((c[0]! * q + c[1]!) * q + c[2]!) * q + c[3]!) * q + c[4]!) * q + c[5]!) /
      ((((d[0]! * q + d[1]!) * q + d[2]!) * q + d[3]!) * q + 1)
    );
  }
};

// =============================================================================
// Beta Distribution Functions
// =============================================================================

/**
 * Log of the Gamma function using Stirling's approximation (Lanczos).
 */
